/**
 * Live prices that are still coming (live supplier pricing spec §6.2, §6.3), headless.
 *
 * A price preview waits a few seconds for each live supplier route. A route still pricing when the wait ends is
 * reported, not failed: beside a priced route it is `priceOptions.pending` on a 200; with nothing priced the preview
 * answers 409 `price_still_coming`, naming the routes (staff) or none (a customer). Either way the screen asks again
 * after `retryAfterSeconds` until every route has answered or turned into no price, and the backend decides when that
 * is. This reads the 409 and runs the asking; it renders nothing.
 */

import { ApiError } from "../data/api/errors";
import type { PendingPriceRoute } from "../data/supplier-pricing-types";
import type { PendingPricesBlock } from "./object-ui/contract";

export const PRICE_STILL_COMING = "price_still_coming";

/** The best route priced so far, offered in place of waiting (Ben, 20 Oct 2026). */
export interface BestPricedRoute {
  key: string;
  routeCode: string;
  routeLabel: string;
  tier: string;
  pricePence: number;
}

/** What a `price_still_coming` 409 says. A customer's names no route and offers no choice. */
export interface PriceStillComing {
  retryAfterSeconds: number;
  pending: PendingPriceRoute[];
  bestPriced: BestPricedRoute | null;
}

interface StillComingDetail {
  reason_code?: string;
  retry_after_seconds?: number;
  pending?: {
    route_code: string;
    route_label: string;
    retry_after_seconds: number;
  }[];
  best_priced_option?: {
    key: string;
    route_code: string;
    route_label: string;
    tier: string;
    price_pence: number;
  } | null;
}

/** The 409 body of a price that is still coming, or null for any other failure. */
export function readPriceStillComing(err: unknown): PriceStillComing | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const body = err.body as { detail?: unknown } | null;
  const detail = body?.detail as StillComingDetail | undefined;
  if (!detail || typeof detail !== "object") return null;
  if (detail.reason_code !== PRICE_STILL_COMING) return null;
  const best = detail.best_priced_option ?? null;
  return {
    retryAfterSeconds: detail.retry_after_seconds ?? 3,
    pending: (detail.pending ?? []).map((p) => ({
      routeCode: p.route_code,
      routeLabel: p.route_label,
      retryAfterSeconds: p.retry_after_seconds,
    })),
    bestPriced: best
      ? {
          key: best.key,
          routeCode: best.route_code,
          routeLabel: best.route_label,
          tier: best.tier,
          pricePence: best.price_pence,
        }
      : null,
  };
}

/** A command dry run's `pending_prices` block (the draft quote's line commands, Order now, Quote now) as the price screens name routes. */
export function pendingRoutesOfBlock(
  block: PendingPricesBlock,
): PendingPriceRoute[] {
  return block.routes.map((route) => ({
    routeCode: route.route_code,
    routeLabel: route.route_label,
    retryAfterSeconds: route.retry_after_seconds,
  }));
}

/** "Solopress: pricing…", the row a pending route shows in the price area. */
export function pendingRouteLine(route: PendingPriceRoute): string {
  return `${route.routeLabel}: pricing…`;
}

function routeNames(pending: readonly PendingPriceRoute[]): string {
  return pending.map((p) => p.routeLabel).join(" and ");
}

/** "In-house is still pricing (usually a few seconds)." */
export function stillPricingSentence(
  pending: readonly PendingPriceRoute[],
): string {
  const verb = pending.length > 1 ? "are" : "is";
  return `${routeNames(pending)} ${verb} still pricing (usually a few seconds).`;
}

/** "Wait for In-house": ask again until the route has answered. */
export function waitForRoutesLabel(
  pending: readonly PendingPriceRoute[],
): string {
  return `Wait for ${routeNames(pending)}`;
}

/** "Waiting for In-house…", shown once the person has chosen to wait. */
export function waitingForRoutesLine(
  pending: readonly PendingPriceRoute[],
): string {
  return `Waiting for ${routeNames(pending)}…`;
}

/** "Use Solopress at £182.00 now": commit on the best route priced so far. */
export function bestPricedNowLabel(
  best: Pick<BestPricedRoute, "routeLabel" | "pricePence">,
  formatPence: (pence: number) => string,
): string {
  return `Use ${best.routeLabel} at ${formatPence(best.pricePence)} now`;
}

/** The longest any route said to wait (else `fallbackSeconds`), clamped so a page neither spins nor hammers the server. */
export function secondsUntilAskingAgain(
  pending: readonly PendingPriceRoute[],
  fallbackSeconds: number,
): number {
  const said =
    pending.length > 0
      ? Math.max(...pending.map((p) => p.retryAfterSeconds))
      : fallbackSeconds;
  return Math.min(Math.max(said, 1), 10);
}

/** How many times a screen asks before it stops waiting and reads the price as unavailable. */
export const MAX_PRICE_ASKS = 8;

export interface AskUntilPricedOptions<T> {
  /** The routes a priced answer still reports as pending (a 200's `priceOptions.pending`). */
  pendingIn?: (answer: T) => readonly PendingPriceRoute[];
  /**
   * Told after each answer that leaves a route pending: the routes, and the answer when there was one (a priced
   * answer with a route still pricing) or null (nothing priced yet).
   */
  onPending?: (pending: readonly PendingPriceRoute[], answer: T | null) => void;
  /** False once the screen has moved on: asking stops and the last answer is returned or thrown. */
  isCurrent?: () => boolean;
  sleep?: (milliseconds: number) => Promise<void>;
  maxAsks?: number;
}

const realSleep = (milliseconds: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, milliseconds));

/**
 * Ask for a price until no route is pending.
 *
 * Returns the first answer with nothing pending. A `price_still_coming` 409 is not a failure: the routes it names
 * go to `onPending` and the ask repeats after the longest `retryAfterSeconds` (1 to 10 seconds). Any other failure
 * is thrown at once. After `maxAsks` the last priced answer is returned (its pending routes still reported), or the
 * 409 is thrown when nothing ever priced, so a stuck supplier cannot keep a screen waiting for ever.
 */
export async function askUntilPriced<T>(
  ask: () => Promise<T>,
  options: AskUntilPricedOptions<T> = {},
): Promise<T> {
  const {
    pendingIn = () => [],
    onPending,
    isCurrent = () => true,
    sleep = realSleep,
    maxAsks = MAX_PRICE_ASKS,
  } = options;
  for (let attempt = 1; ; attempt++) {
    let answer: T | null = null;
    let pending: readonly PendingPriceRoute[];
    let retryAfterSeconds: number;
    try {
      answer = await ask();
      pending = pendingIn(answer);
      retryAfterSeconds = 3;
    } catch (err) {
      const stillComing = readPriceStillComing(err);
      if (stillComing === null) throw err;
      pending = stillComing.pending;
      retryAfterSeconds = stillComing.retryAfterSeconds;
      if (attempt >= maxAsks || !isCurrent()) throw err;
    }
    if (answer !== null && pending.length === 0) return answer;
    if (answer !== null && (attempt >= maxAsks || !isCurrent())) return answer;
    onPending?.(pending, answer);
    await sleep(secondsUntilAskingAgain(pending, retryAfterSeconds) * 1000);
  }
}
